import styled from '@emotion/styled';
import {
  Box,
  Button,
  ButtonGroup,
  ClickAwayListener,
  keyframes,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

import React from 'react';
import { useUpdates } from 'src/Modules/Arbitrage/context/updatesContext';
import { RestartConnectorsDialog } from './RestartConnectorsDialog';
import { Subsystem } from 'src/Api/exchanges';
import { JobProgressStepper } from 'src/Common/components/JobProgressStepper/JobProgressStepper';

interface ToggleUpdatesButtonProps {
  subsystem: Subsystem;
}

export const ToggleUpdatesButton = ({
  subsystem
}: ToggleUpdatesButtonProps) => {
  const {
    isOrderbooksRunning,
    isPredictionsRunning,
    toggleSystem,
    restartConnectors,
    isToggleLoading,
    isRestartLoading,
    currentJob,
    isJobRunning
  } = useUpdates();

  const isRunning =
    subsystem === 'orderbooks'
      ? isOrderbooksRunning
      : subsystem === 'predictions'
        ? isPredictionsRunning
        : isOrderbooksRunning || isPredictionsRunning;

  // Show progress if the current job matches this subsystem (running OR just completed)
  const isMyJob = Boolean(
    currentJob &&
      currentJob.subsystem === subsystem &&
      (currentJob.status === 'running' ||
        (currentJob.completedAt &&
          Date.now() - currentJob.completedAt < 5000))
  );

  const anchorEl = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  const [restartDialogOpen, setRestartDialogOpen] = React.useState(false);

  // Auto-dismiss the progress popover 3s after job completes
  const [showJobResult, setShowJobResult] = React.useState(false);
  const prevJobRunning = React.useRef(false);

  React.useEffect(() => {
    if (isMyJob) {
      prevJobRunning.current = true;
      setShowJobResult(true);
    } else if (prevJobRunning.current && !isMyJob) {
      prevJobRunning.current = false;
      const timer = setTimeout(() => setShowJobResult(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isMyJob]);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorEl.current &&
      anchorEl.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setOpen(false);
  };

  const handleOpenRestartDialog = () => {
    setOpen(false);
    setRestartDialogOpen(true);
  };

  const handleRestart = async (exchanges: string[]) => {
    await restartConnectors(exchanges.length > 0 ? exchanges : undefined);
  };

  const showProgress = (isMyJob || showJobResult) && currentJob;

  const label =
    subsystem === 'predictions' ? 'Predictions' : 'Orderbooks';

  return (
    <Box sx={{ position: 'relative' }}>
      <ButtonGroup ref={anchorEl}>
        <Tooltip
          title={
            isMyJob
              ? `${currentJob?.type === 'start' ? 'Starting' : 'Stopping'} ${label}...`
              : isRunning
                ? `Turn OFF ${label}`
                : `Turn ON ${label}`
          }
        >
          <LiveButton
            loading={isToggleLoading && isMyJob}
            onClick={() => toggleSystem(subsystem)}
            size="small"
            variant="outlined"
            disabled={isJobRunning || (isMyJob && currentJob?.status === 'running')}
            endIcon={
              isMyJob && currentJob?.status === 'running' ? (
                <SpinnerIcon />
              ) : isRunning ? (
                <DotIcon />
              ) : (
                <DotIconEmpty />
              )
            }
          >
            {isMyJob && currentJob?.status === 'running'
              ? currentJob?.type === 'start'
                ? 'Starting'
                : 'Stopping'
              : isRunning
                ? 'On'
                : 'Off'}
          </LiveButton>
        </Tooltip>

        {subsystem === 'orderbooks' && (
          <>
            <UpdatesButtonOptions
              color="primary"
              size="small"
              variant="outlined"
            >
              <ArrowDropDownIcon onClick={handleToggle} />
            </UpdatesButtonOptions>

            <Popper
              sx={{ zIndex: 1300 }}
              open={open}
              anchorEl={anchorEl.current}
              placement="bottom-end"
            >
              <Paper elevation={8}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList id="split-button-menu" autoFocusItem dense>
                    <MenuItem
                      onClick={handleOpenRestartDialog}
                      disabled={
                        isRestartLoading || !isRunning || isJobRunning
                      }
                    >
                      <ListItemIcon>
                        <RestartAltIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText>Restart Connectors</ListItemText>
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Popper>
          </>
        )}
      </ButtonGroup>

      {subsystem === 'orderbooks' && (
        <RestartConnectorsDialog
          open={restartDialogOpen}
          onClose={() => setRestartDialogOpen(false)}
          onRestart={handleRestart}
          isLoading={isRestartLoading}
          disabled={!isRunning || isJobRunning}
        />
      )}

      {/* Progress indicator */}
      {showProgress && currentJob && (
        <ProgressContainer>
          <Paper
            elevation={6}
            sx={{
              p: 1.5,
              minWidth: 280,
              maxWidth: 320,
              bgcolor: 'background.paper'
            }}
          >
            <JobProgressStepper job={currentJob} label={label} />
          </Paper>
        </ProgressContainer>
      )}
    </Box>
  );
};

// ============================================================================
// Styled components
// ============================================================================

const ProgressContainer = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 8px;
  z-index: 1400;
`;

const bounce = keyframes`
    0% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(62, 228, 55, 0.7);
    }

    70% {
      transform: scale(1);
      box-shadow: 0 0 0 5px rgba(62, 228, 55, 0);
    }

    100% {
      transform: scale(0.95);
      box-shadow: 0 0 0 0 rgba(62, 228, 55, 0);
    }
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const DotIcon = styled.div(() => ({
  background: 'rgba(62, 228, 55)',
  borderRadius: '50%',
  height: '.4rem',
  width: '.4rem',
  boxShadow: '0 0 0 0 rgba(62, 228, 55)',
  transform: 'scale(1)',
  animation: `${bounce} 2s ease infinite`
}));

const DotIconEmpty = styled.div((props: any) => ({
  borderRadius: '50%',
  height: '.4rem',
  width: '.4rem',
  backgroundColor: props.theme.palette.error.main
}));

const SpinnerIcon = styled.div(() => ({
  width: '.5rem',
  height: '.5rem',
  border: '1.5px solid rgba(255,255,255,0.3)',
  borderTop: '1.5px solid #fff',
  borderRadius: '50%',
  animation: `${spin} 0.8s linear infinite`
}));

const LiveButton = styled(LoadingButton)((props: any) => ({
  borderRadius: '20rem',
  padding: '0.2rem 1rem 0.2rem 1rem',
  color: 'white',
  borderColor: props.theme.palette.grey[600],
  borderWidth: '0.5px',
  fontSize: '.6rem'
}));

const UpdatesButtonOptions = styled(Button)((props: any) => ({
  borderRadius: '20rem',
  padding: 0,
  color: 'white',
  borderColor: props.theme.palette.grey[600],
  borderWidth: '0.5px',
  fontSize: '.6rem',
  whiteSpace: 'nowrap'
}));
